/**
 * card-product.js
 * ------------------------------------------------------------------
 * Main controller for the <farfhe-product-card> custom element.
 *
 * Architecture (split out of the old monolithic card-product-farfhe.js):
 *   - card-variant.js       → CardVariantResolver: pure state/matching logic
 *   - card-add-to-cart.js   → CardAddToCart: POST /cart/add.js + cart UI refresh
 *   - card-product.js (this file) → DOM wiring: reads inline product JSON,
 *     owns one CardVariantResolver instance per card, reflects its output
 *     into the DOM, and is the only place that touches both modules.
 *
 * Key change from the old version: product data is now embedded
 * server-side as JSON (see card-product-farfhe.liquid's
 * <script type="application/json" data-product-json>) instead of being
 * fetched over the network after first interaction. This removes the
 * async data-loading window entirely — selectedOptions, in particular
 * the color the user just clicked, can never be evaluated against
 * stale/partial product data because there is no longer a "data not
 * loaded yet" state once the element exists in the DOM.
 *
 * The original color→size desync bug is fixed at the resolver level
 * (CardVariantResolver.resyncOption), called every time color changes,
 * so a size that isn't valid for the new color is never silently left
 * selected.
 */
(function () {
  if (customElements.get('farfhe-product-card')) return;

  customElements.define(
    'farfhe-product-card',
    class CardProduct extends HTMLElement {
      constructor() {
        super();

        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

        this.priceEl        = this.querySelector('[data-price]');
        this.comparePriceEl = this.querySelector('[data-compare-price]');
        this.discountEl     = this.querySelector('[data-discount]');
        this.primaryImage   = this.querySelector('[data-card-image="primary"]');
        this.quickAddButton = this.querySelector('.farfhe-quick-add__button');
        this.swatchesGroup  = this.querySelector('.farfhe-card__swatches');
        this.sizesGroup     = this.querySelector('.farfhe-card__sizes');

        // In-card image slider (Horizon-style dots / swipe / arrows)
        this.slides   = Array.from(this.querySelectorAll('.farfhe-card__image[data-slide-index]'));
        this.dots     = Array.from(this.querySelectorAll('.farfhe-dot'));
        this.slideIdx = 0;

        this.resolver = new window.CardVariantResolver(this._readProductData());

        // Seed selectedOptions from server-rendered defaults (works
        // immediately — no async gap to race against).
        const seed = {};
        [this.swatchesGroup, this.sizesGroup].filter(Boolean).forEach((group) => {
          const active = group.querySelector('[aria-pressed="true"]');
          if (active) {
            seed[group.dataset.optionName] = active.dataset.swatchValue || active.dataset.sizeValue;
          }
        });
        this.resolver.seedOptions(seed);

        this.addEventListener('click', this._onClick.bind(this));
        this._initSlider();

        if (this.resolver.currentVariant) {
          this._reflectVariant();
        }
      }

      /* ----------------------------------------------------------
       * In-card image slider: dot nav, arrow nav, touch swipe.
       * Pure DOM cross-fade between pre-rendered <img> slides —
       * no extra network requests, images are already in the DOM.
       * ---------------------------------------------------------- */
      _initSlider() {
        if (this.slides.length < 2) return;

        const slider = this.querySelector('[data-card-slider]');
        if (slider) {
          let touchStartX = null;
          slider.addEventListener('touchstart', (e) => {
            touchStartX = e.touches[0].clientX;
          }, { passive: true });
          slider.addEventListener('touchend', (e) => {
            if (touchStartX == null) return;
            const deltaX = e.changedTouches[0].clientX - touchStartX;
            touchStartX = null;
            if (Math.abs(deltaX) < 40) return;
            this._goToSlide(deltaX < 0 ? this.slideIdx + 1 : this.slideIdx - 1);
          }, { passive: true });
        }
      }

      _goToSlide(index) {
        if (!this.slides.length) return;
        const next = (index + this.slides.length) % this.slides.length;
        if (next === this.slideIdx) return;

        this.slides[this.slideIdx]?.classList.remove('farfhe-card__image--active');
        this.dots[this.slideIdx]?.classList.remove('farfhe-dot--active');
        this.dots[this.slideIdx]?.setAttribute('aria-selected', 'false');

        this.slideIdx = next;

        this.slides[this.slideIdx]?.classList.add('farfhe-card__image--active');
        this.dots[this.slideIdx]?.classList.add('farfhe-dot--active');
        this.dots[this.slideIdx]?.setAttribute('aria-selected', 'true');
      }

      /* ----------------------------------------------------------
       * Read + parse the inline product JSON rendered by Liquid.
       * No network request, no cache, no race condition — the data
       * is present the instant the element is constructed.
       * ---------------------------------------------------------- */
      _readProductData() {
        const script = this.querySelector('script[data-product-json]');
        if (!script) {
          console.error('card-product: missing inline product JSON', this);
          return null;
        }
        try {
          return JSON.parse(script.textContent);
        } catch (err) {
          console.error('card-product: failed to parse inline product JSON', err, this);
          return null;
        }
      }

      /* ----------------------------------------------------------
       * Delegated click handler
       * ---------------------------------------------------------- */
      _onClick(e) {
        const dot = e.target.closest('.farfhe-dot');
        if (dot) {
          e.preventDefault();
          this._goToSlide(Number(dot.dataset.dotIndex));
          return;
        }

        const arrow = e.target.closest('[data-card-prev], [data-card-next]');
        if (arrow) {
          e.preventDefault();
          this._goToSlide(this.slideIdx + (arrow.hasAttribute('data-card-next') ? 1 : -1));
          return;
        }

        const swatch = e.target.closest('[data-swatch-value]');
        if (swatch) {
          e.preventDefault();
          this._selectSwatch(swatch);
          return;
        }

        const size = e.target.closest('[data-size-value]');
        if (size && !size.disabled && !size.dataset.choosingOptions) {
          e.preventDefault();
          this._selectSizeAndAdd(size);
          return;
        }

        const directAtc = e.target.closest('.farfhe-quick-add--variant-direct');
        if (directAtc && !directAtc.disabled) {
          e.preventDefault();
          this._addCurrentVariant(directAtc);
          return;
        }
      }

      /* ----------------------------------------------------------
       * Color swatch click → update resolver, reflect DOM, then
       * re-sync the size selection against the new color so a stale/
       * invalid size can never stay selected.
       * ---------------------------------------------------------- */
      _selectSwatch(swatch) {
        const group = swatch.closest('[data-option-name]');
        if (!group) return;

        group.querySelectorAll('button').forEach((b) => {
          b.classList.remove('farfhe-swatch--active');
          b.setAttribute('aria-pressed', 'false');
        });
        swatch.classList.add('farfhe-swatch--active');
        swatch.setAttribute('aria-pressed', 'true');

        this.resolver.setOption(group.dataset.optionName, swatch.dataset.swatchValue);
        this._syncSizeSelectionToColor();
        this._reflectVariant();
      }

      _syncSizeSelectionToColor() {
        if (!this.sizesGroup) return;
        const sizeName = this.sizesGroup.dataset.optionName;
        const chips = Array.from(this.sizesGroup.querySelectorAll('[data-size-value]'));
        const candidateValues = chips.map((chip) => chip.dataset.sizeValue);

        const resolvedValue = this.resolver.resyncOption(sizeName, candidateValues);

        chips.forEach((chip) => {
          const isSelected = resolvedValue != null && chip.dataset.sizeValue === resolvedValue;
          chip.classList.toggle('farfhe-size--active', isSelected);
          chip.setAttribute('aria-pressed', String(isSelected));
        });

        this._updateSizeAvailability();
      }

      /* ----------------------------------------------------------
       * Size click → update resolver, add to cart from the resolved
       * currentVariant (never re-derived from the DOM).
       * ---------------------------------------------------------- */
      async _selectSizeAndAdd(sizeBtn) {
        if (sizeBtn.classList.contains('farfhe-size--loading')) return;

        const group = sizeBtn.closest('[data-option-name]');
        const originalText = sizeBtn.textContent.trim();

        sizeBtn.classList.add('farfhe-size--loading');
        sizeBtn.textContent = '…';

        if (group) {
          group.querySelectorAll('.farfhe-size').forEach((b) => {
            b.classList.remove('farfhe-size--active');
            b.setAttribute('aria-pressed', 'false');
          });
          sizeBtn.classList.add('farfhe-size--active');
          sizeBtn.setAttribute('aria-pressed', 'true');
          this.resolver.setOption(group.dataset.optionName, sizeBtn.dataset.sizeValue);
        }

        try {
          const variant = this.resolver.currentVariant;
          console.debug('card-product (size):', {
            selectedOptions: { ...this.resolver.selectedOptions },
            variantId: variant && variant.id,
            variantOptions: variant && [variant.option1, variant.option2, variant.option3],
          });

          if (!variant) throw new Error('Variant not found for ' + JSON.stringify(this.resolver.selectedOptions));
          if (!variant.available) throw new Error('Variant unavailable');

          await window.CardAddToCart.add(variant.id, 1);

          sizeBtn.textContent = '✓';
          sizeBtn.classList.add('farfhe-size--success');
          sizeBtn.classList.remove('farfhe-size--loading');

          setTimeout(() => {
            sizeBtn.textContent = originalText;
            sizeBtn.classList.remove('farfhe-size--success');
          }, 1500);
        } catch (err) {
          console.error('card-product (size) add failed:', err);
          sizeBtn.textContent = '✗';
          sizeBtn.classList.add('farfhe-size--error');
          sizeBtn.classList.remove('farfhe-size--loading', 'farfhe-size--active');
          sizeBtn.setAttribute('aria-pressed', 'false');
          setTimeout(() => {
            sizeBtn.textContent = originalText;
            sizeBtn.classList.remove('farfhe-size--error');
          }, 1500);
        }
      }

      /* ----------------------------------------------------------
       * Direct ATC for any product whose overlay has no size chips:
       * true single-variant products, color-only products (uses
       * whatever color swatch is currently selected via the
       * resolver), or any other option combo. Never opens a modal.
       * ---------------------------------------------------------- */
      async _addCurrentVariant(btn) {
        if (btn.classList.contains('loading')) return;

        const variantId = this.resolver.currentVariant?.id || btn.dataset.variantId || this.dataset.variantId;
        if (!variantId) {
          console.error('card-product: missing variant id for single-variant add to cart', this);
          return;
        }

        const label = btn.querySelector('.farfhe-quick-add__label');
        const spinner = btn.querySelector('.loading__spinner');
        const originalText = label ? label.textContent.trim() : '';

        btn.classList.add('loading');
        btn.setAttribute('aria-disabled', 'true');
        if (spinner) spinner.classList.remove('hidden');

        try {
          await window.CardAddToCart.add(Number(variantId), 1);

          if (label) label.textContent = '✓';
          btn.classList.remove('loading');
          btn.classList.add('farfhe-atc--success');
          if (spinner) spinner.classList.add('hidden');

          setTimeout(() => {
            if (label) label.textContent = originalText;
            btn.classList.remove('farfhe-atc--success');
            btn.removeAttribute('aria-disabled');
          }, 1500);
        } catch (err) {
          if (label) label.textContent = '✗';
          btn.classList.remove('loading');
          btn.classList.add('farfhe-atc--error');
          if (spinner) spinner.classList.add('hidden');

          setTimeout(() => {
            if (label) label.textContent = originalText;
            btn.classList.remove('farfhe-atc--error');
            btn.removeAttribute('aria-disabled');
          }, 1500);
        }
      }

      /* ----------------------------------------------------------
       * Push every dependent UI update from the resolved variant.
       * ---------------------------------------------------------- */
      _reflectVariant() {
        const variant = this.resolver.currentVariant;
        if (!variant) return;

        this.dataset.variantId = variant.id;
        if (this.quickAddButton) this.quickAddButton.dataset.variantId = variant.id;

        this._updateModalUrls(variant);
        this._updatePriceAndImage(variant);
        this._updateSizeAvailability();
      }

      _updateModalUrls(variant) {
        const base = this.dataset.productUrl || '';
        if (!base) return;
        const [path] = base.split('?');
        const variantUrl = `${path}?variant=${variant.id}`;
        this.querySelectorAll('[data-product-url]').forEach((el) => {
          el.setAttribute('data-product-url', variantUrl);
        });
      }

      _updatePriceAndImage(variant) {
        if (this.priceEl) {
          this.priceEl.textContent = window.CardAddToCart.formatMoney(variant.price, this.moneyFormat);
        }
        const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (this.comparePriceEl) {
          this.comparePriceEl.textContent = hasCompare
            ? window.CardAddToCart.formatMoney(variant.compare_at_price, this.moneyFormat) : '';
          this.comparePriceEl.hidden = !hasCompare;
        }
        if (this.discountEl) {
          if (hasCompare) {
            const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
            this.discountEl.textContent = `-${pct}%`;
            this.discountEl.hidden = false;
          } else {
            this.discountEl.textContent = '';
            this.discountEl.hidden = true;
          }
        }

        if (this.primaryImage && variant.featured_image) {
          const src = typeof variant.featured_image === 'string'
            ? variant.featured_image : variant.featured_image.src;
          if (src) {
            const widths = [165, 360, 533, 720, 940];
            this.primaryImage.srcset = widths
              .map((w) => `${src.includes('?') ? src + '&' : src + '?'}width=${w} ${w}w`)
              .join(', ');
            this.primaryImage.src = `${src.includes('?') ? src + '&' : src + '?'}width=533`;
          }
          // New color → snap the slider back to its main photo (slide 0)
          this._goToSlide(0);
        }
      }

      /* ----------------------------------------------------------
       * Grey out sizes that aren't available for the currently
       * selected color (and any other selected options).
       * ---------------------------------------------------------- */
      _updateSizeAvailability() {
        if (!this.sizesGroup) return;
        const sizeName = this.sizesGroup.dataset.optionName;
        const availability = this.resolver.getAvailabilityMap(sizeName);

        this.sizesGroup.querySelectorAll('[data-size-value]').forEach((chip) => {
          const available = !!availability[chip.dataset.sizeValue];
          chip.disabled = !available;
          chip.setAttribute('aria-disabled', String(!available));
        });
      }
    }
  );
})();
